<template>
  <div class="barcode-pool-page">
    <!-- Hero header -->
    <header class="bp-hero">
      <div class="bp-hero__subtitle">
        <strong>{{ formatNumber(totalRecords) }}</strong>
        {{ $t('active ranges') }}
        <span class="bp-dot">·</span>
        <strong>{{ summary ? formatNumber(summary.total) : '—' }}</strong>
        {{ $t('total barcodes') }}
      </div>
      <div class="bp-hero__actions">
        <b-button variant="primary" size="xs" @click="openCreateModal">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          {{ $t('Generate Barcode Range') }}
        </b-button>
      </div>
    </header>

    <!-- Summary cards -->
    <b-row v-if="summary" class="bp-summary match-height">
      <b-col sm="6" lg="3">
        <article class="bp-stat-card bp-stat-card--success">
          <div class="bp-stat-card__head">
            <div class="bp-stat-card__icon">
              <feather-icon icon="PackageIcon" size="20" />
            </div>
            <b-badge :variant="summaryAlertVariant" pill class="bp-stat-card__badge">
              {{ $t(`Alert: ${summary.alert_level}`) }}
            </b-badge>
          </div>
          <div class="bp-stat-card__body">
            <div class="bp-stat-card__value">
              {{ formatNumber(summary.available) }}
              <span class="bp-stat-card__pct">{{ summary.available_pct }}%</span>
            </div>
            <div class="bp-stat-card__label">{{ $t('Available') }}</div>
            <div class="bp-stat-card__desc">
              {{ formatNumber(summary.total) }} {{ $t('of total') }}
            </div>
          </div>
        </article>
      </b-col>
      <b-col sm="6" lg="3">
        <article class="bp-stat-card bp-stat-card--warning">
          <div class="bp-stat-card__head">
            <div class="bp-stat-card__icon">
              <feather-icon icon="LockIcon" size="20" />
            </div>
          </div>
          <div class="bp-stat-card__body">
            <div class="bp-stat-card__value">{{ formatNumber(summary.reserved) }}</div>
            <div class="bp-stat-card__label">{{ $t('Reserved') }}</div>
            <div class="bp-stat-card__desc">{{ $t('Locked to active consignments') }}</div>
          </div>
        </article>
      </b-col>
      <b-col sm="6" lg="3">
        <article class="bp-stat-card bp-stat-card--primary">
          <div class="bp-stat-card__head">
            <div class="bp-stat-card__icon">
              <feather-icon icon="SendIcon" size="20" />
            </div>
          </div>
          <div class="bp-stat-card__body">
            <div class="bp-stat-card__value">{{ formatNumber(summary.used) }}</div>
            <div class="bp-stat-card__label">{{ $t('Used') }}</div>
            <div class="bp-stat-card__desc">{{ $t('Dispatched to carrier') }}</div>
          </div>
        </article>
      </b-col>
      <b-col sm="6" lg="3">
        <article class="bp-stat-card bp-stat-card--dark">
          <div class="bp-stat-card__head">
            <div class="bp-stat-card__icon">
              <feather-icon icon="SlashIcon" size="20" />
            </div>
          </div>
          <div class="bp-stat-card__body">
            <div class="bp-stat-card__value">{{ formatNumber(summary.skipped) }}</div>
            <div class="bp-stat-card__label">{{ $t('Skipped') }}</div>
            <div class="bp-stat-card__desc">{{ $t('Unusable') }}</div>
          </div>
        </article>
      </b-col>
    </b-row>

    <!-- Ranges section -->
    <b-card no-body class="bp-ranges-card">
      <div class="bp-ranges-card__header" sty>
        <div class="d-flex align-items-center" style="gap: 0.75rem">
          <h4 class="mb-0">{{ $t('Ranges') }}</h4>
          <b-badge variant="light" class="bp-count-pill">
            {{ formatNumber(totalRecords) }}
          </b-badge>
        </div>
        <div class="bp-ranges-card__actions">
          <b-input-group class="bp-search">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" size="14" />
            </b-input-group-prepend>
            <b-form-input
              v-model="quickSearch"
              :placeholder="$t('Search customer or barcode...')"
              @input="onQuickSearch"
            />
          </b-input-group>
          <!-- <b-button v-if="isAdmin" variant="outline-danger" @click="openPurgeModal">
            <feather-icon icon="Trash2Icon" size="14" class="mr-25" />
            {{ $t('Purge Available Barcodes') }}
          </b-button> -->
        </div>
      </div>

      <div class="bp-ranges-card__filter">
        <barcode-pool-filter
          :values="filterValues"
          :provider-options="providerOptions"
          :customer-options="customerOptions"
          :show-customer="isAdmin"
          @filter="setFilter"
        />
      </div>

      <!-- Error state — takes precedence over empty/data -->
      <div v-if="loadError && !isLoading" class="bp-error">
        <feather-icon icon="AlertCircleIcon" size="40" class="bp-error__icon" />
        <h5 class="mb-50">{{ $t('Could not load ranges') }}</h5>
        <p class="text-muted mb-1">{{ loadError }}</p>
        <b-button variant="outline-primary" size="sm" @click="loadItems">
          <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
          {{ $t('Retry') }}
        </b-button>
      </div>

      <!-- Empty state -->
      <div v-else-if="!isLoading && !rows.length && !hasFilters" class="bp-empty">
        <feather-icon icon="DatabaseIcon" size="56" class="bp-empty__icon" />
        <h4>{{ $t('No Range Yet Title') }}</h4>
        <p class="text-muted mb-2">{{ $t('No Range Yet Desc') }}</p>
        <b-button variant="primary" @click="openCreateModal">
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          {{ $t('Generate Barcode Range') }}
        </b-button>
      </div>

      <vue-good-table
        v-else
        mode="remote"
        styleClass="vgt-table bp-table"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :rows="rows"
        :columns="columns"
        :pagination-options="paginationOptions"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <table-no-result-found slot="emptystate" />
        <template slot="loadingContent">
          <span class="vgt-loading__content">{{ $t('Loading...') }}</span>
        </template>
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'customer_name'">
            <router-link
              v-if="props.row.customer_id"
              :to="{ name: 'customers_detail', params: { id: props.row.customer_id } }"
              class="bp-ref-link"
            >
              {{ props.row.customer_name }}
            </router-link>
            <span v-else class="font-weight-bold">
              {{ props.row.customer_name || '-' }}
            </span>
          </span>

          <span v-else-if="props.column.field === 'provider'" class="bp-provider-cell">
            <img
              :src="props.row._providerLogo"
              :alt="props.row.provider_name"
              class="bp-provider-cell__logo"
            />
            <span class="bp-provider-cell__text">
              <span class="bp-provider-cell__name">
                {{ props.row.provider_name }}
              </span>
              <span class="bp-provider-cell__type">
                {{ formatBarcodeType(props.row.barcode_type) }}
              </span>
            </span>
          </span>

          <span v-else-if="props.column.field === 'range'" class="bp-range-cell">
            <span class="bp-range-cell__start">{{ props.row.range_start }}</span>
            <span class="bp-range-cell__end">→ {{ props.row.range_end }}</span>
          </span>

          <span v-else-if="props.column.field === 'total_count'" class="font-weight-bold">
            {{ formatNumber(props.row.total_count) }}
          </span>

          <span v-else-if="props.column.field === 'available_count'" class="bp-available-cell">
            <div class="font-weight-bold">
              {{ formatNumber(props.row.available_count) }}
            </div>
            <small class="text-muted">{{ props.row._availablePct }}%</small>
          </span>

          <span v-else-if="props.column.field === 'consumption'" class="bp-consumption-cell">
            <div class="bp-progress" :class="{ 'bp-progress--completed': props.row._isCompleted }">
              <div class="bp-progress__used" :style="{ width: `${props.row._usedPct}%` }" />
              <div class="bp-progress__reserved" :style="{ width: `${props.row._reservedPct}%` }" />
            </div>
            <b-badge :variant="props.row._alertVariant" class="bp-consumption-cell__pill" pill>
              <span class="bp-consumption-cell__dot"></span>
              {{ $t(`Alert: ${props.row._alertLevel}`) }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'status'">
            <b-badge
              :variant="props.row.status === 'active' ? 'light-success' : 'light-dark'"
              class="bp-status-badge"
            >
              <span
                class="bp-status-badge__dot"
                :class="`bp-status-badge__dot--${props.row.status}`"
              ></span>
              {{ $t(props.row.status === 'active' ? 'Active' : 'Exhausted') }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'created_at'" class="text-nowrap">
            {{ formatDate(props.row.created_at) }}
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <b-dropdown
              size="sm"
              variant="link"
              toggle-class="text-decoration-none p-0 bp-kebab"
              :toggle-attrs="{ 'aria-label': $t('Row actions') }"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="18" />
              </template>
              <b-dropdown-item
                :to="{
                  name: 'barcode_pool_detail',
                  params: { id: props.row.id }
                }"
              >
                <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                {{ $t('Detail') }}
              </b-dropdown-item>
              <b-dropdown-divider v-if="isAdmin && !props.row.deleted_at" />
              <b-dropdown-item
                v-if="isAdmin && !props.row.deleted_at"
                variant="danger"
                @click="confirmDelete(props.row)"
              >
                <feather-icon icon="Trash2Icon" size="14" class="mr-50" />
                {{ $t('Delete Range') }}
              </b-dropdown-item>
            </b-dropdown>
          </span>

          <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
        </template>
      </vue-good-table>
    </b-card>

    <!-- Create modal -->
    <create-barcode-range-modal
      ref="createModal"
      :provider-options="providerOptions"
      :customer-options="customerOptions"
      @created="onRangeCreated"
    />

    <!-- Purge modal — admin only -->
    <!-- <b-modal
      v-if="isAdmin"
      v-model="purgeModal.show"
      :title="t('Purge Available Barcodes')"
      :ok-title="t('Delete')"
      :cancel-title="t('Cancel')"
      ok-variant="danger"
      no-close-on-backdrop
      @ok.prevent="submitPurge"
    >
      <b-form-group :label="t('Customer')">
        <v-select
          v-model="purgeModal.customer_id"
          :options="customerOptions"
          :reduce="opt => opt.value"
          :placeholder="t('Select Customer')"
        />
      </b-form-group>
      <b-form-group :label="t('Provider')">
        <v-select
          v-model="purgeModal.provider_id"
          :options="providerOptions"
          :reduce="opt => opt.value"
          :placeholder="t('Select Provider')"
        />
      </b-form-group>
    </b-modal> -->
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BRow,
  BCol,
  BBadge,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BDropdown,
  BDropdownItem,
  BDropdownDivider
} from 'bootstrap-vue';
import debounce from 'lodash.debounce';
import { VueGoodTable } from 'vue-good-table';
import { mapGetters } from 'vuex';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import useService from '@/auth/service/useService';
import TableNoResultFound from '@/components/TableNoResultFound.vue';
import BarcodePoolFilter from '@/views/pages/barcode-pool/BarcodePoolFilter.vue';
import CreateBarcodeRangeModal from '@/views/pages/barcode-pool/CreateBarcodeRangeModal.vue';
import barcodePoolFormatters from '@/views/pages/barcode-pool/barcodePoolFormatters.mixin';
import { getProviderLogo } from '@/libs/providerHelper';
import {
  BARCODE_POOL_PROVIDER_CODES,
  availablePct,
  computeAlertLevel,
  getAlertVariant,
  segmentPct
} from '@/constants/barcodePool';

function decorateRow(row) {
  const alertLevel = computeAlertLevel(row);
  return {
    ...row,
    _alertLevel: alertLevel,
    _alertVariant: getAlertVariant(alertLevel),
    _availablePct: availablePct(row),
    _usedPct: segmentPct(row, 'used_count'),
    _reservedPct: segmentPct(row, 'reserved_count'),
    _isCompleted: alertLevel === 'completed',
    _providerLogo: getProviderLogo({
      id: row.provider_id,
      provider_code: row.provider_code
    })
  };
}

const emptyPurgeForm = () => ({
  show: false,
  customer_id: null,
  provider_id: null
});

const DEFAULT_PAGE_SIZE = 10;
const QUICK_SEARCH_DEBOUNCE_MS = 350;

export default {
  name: 'BarcodePoolList',

  components: {
    BButton,
    BCard,
    BRow,
    BCol,
    BBadge,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    VueGoodTable,
    TableNoResultFound,
    BarcodePoolFilter,
    CreateBarcodeRangeModal
  },

  mixins: [helper, barcodePoolFormatters],

  data() {
    return {
      isLoading: true,
      loadError: null,
      rows: [],
      totalRecords: 0,
      summary: null,
      quickSearch: '',
      filter: {
        page: 1,
        limit: DEFAULT_PAGE_SIZE
      },
      filterValues: {},
      purgeModal: emptyPurgeForm(),
      providerOptions: [],
      customerOptions: []
    };
  },

  computed: {
    ...mapGetters('auth', ['isAdmin']),

    hasFilters() {
      return Object.keys(this.filterValues).length > 0 || !!this.quickSearch;
    },

    summaryAlertVariant() {
      return this.summary ? getAlertVariant(this.summary.alert_level) : null;
    },

    columns() {
      const cols = [];
      if (this.isAdmin) {
        cols.push({ label: this.$t('Customer'), field: 'customer_name' });
      }
      cols.push(
        { label: this.$t('Provider'), field: 'provider' },
        { label: this.$t('Barcode Range'), field: 'range' },
        { label: this.$t('Total'), field: 'total_count' },
        { label: this.$t('Available'), field: 'available_count' },
        { label: this.$t('Consumption'), field: 'consumption', width: '220px' },
        { label: this.$t('Status'), field: 'status' },
        { label: this.$t('Created At'), field: 'created_at' },
        { label: '', field: 'actions', width: '60px' }
      );
      return cols;
    },

    barcodeTypeOptions() {
      return [
        { label: this.$t('Small Package'), value: 'small' },
        { label: this.$t('Large Package'), value: 'large' }
      ];
    },

    paginationOptions() {
      return this.buildPaginationOptions(this.filter.limit);
    }
  },

  created() {
    this.onQuickSearch = debounce(() => {
      this.filter.page = 1;
      this.loadItems();
    }, QUICK_SEARCH_DEBOUNCE_MS);
  },

  beforeDestroy() {
    if (this.onQuickSearch && this.onQuickSearch.cancel) {
      this.onQuickSearch.cancel();
    }
  },

  mounted() {
    this.loadItems();
    this.loadLookups();
  },

  methods: {
    loadLookups() {
      useService
        .getAllProviders({ paginated: false })
        .then(res => {
          this.providerOptions = (res.data || [])
            .filter(p => BARCODE_POOL_PROVIDER_CODES.includes(p.code || p.name))
            .map(p => ({
              label: p.name,
              value: p.id,
              code: p.code || p.name
            }));
        })
        .catch(() => {});

      if (!this.isAdmin) return;

      this.getCustomers({ paginated: false }).then(data => {
        this.customerOptions = (data || []).map(c => ({
          label: c.name,
          value: c.id
        }));
      });
    },

    loadItems() {
      const requestData = {
        ...this.filter,
        ...this.getFilteredObject(this.filterValues)
      };
      if (this.quickSearch) requestData.q = this.quickSearch;

      this.isLoading = true;
      this.loadError = null;

      const apiCall = this.isAdmin
        ? useService.barcodePoolService.listBarcodeRangesAdmin(requestData)
        : useService.barcodePoolService.listBarcodeRangesCustomer(requestData);

      apiCall
        .then(response => {
          this.rows = (response.data || []).map(decorateRow);
          this.totalRecords = response.meta?.total || 0;
          this.summary = response.summary || null;
        })
        .catch(messages => {
          this.loadError = this.extractErrorMessage(messages);
          this.rows = [];
          this.totalRecords = 0;
          this.summary = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onPageChange({ currentPage }) {
      this.filter.page = currentPage;
      this.loadItems();
    },

    onPerPageChange({ currentPerPage }) {
      this.filter.page = 1;
      this.filter.limit = currentPerPage;
      this.loadItems();
    },

    setFilter(data) {
      this.filterValues = data || {};
      this.filter.page = 1;
      this.loadItems();
    },

    openCreateModal() {
      this.$refs.createModal.show();
    },

    onRangeCreated() {
      this.filter.page = 1;
      this.loadItems();
    },

    openPurgeModal() {
      this.purgeModal = { ...emptyPurgeForm(), show: true };
    },

    submitPurge() {
      const { customer_id, provider_id } = this.purgeModal;
      if (!customer_id || !provider_id) {
        this.showErrorMessage(this.$t('Please fill all required fields'));
        return;
      }

      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: this.$t('Purge Confirmation'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes'),
        cancelButtonText: this.$t('No'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false
      }).then(result => {
        if (!result.value) return;

        const payload = { customer_id, provider_id };

        useService.barcodePoolService
          .purgeBarcodePool(payload)
          .then(response => {
            this.showSuccessMessage(response.message || this.$t('Successful'));
            this.purgeModal.show = false;
            this.loadItems();
          })
          .catch(messages => {
            this.showBackendErrors(messages);
          });
      });
    },

    confirmDelete(row) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: this.$t('Delete Range Confirmation'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes'),
        cancelButtonText: this.$t('No'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false
      }).then(result => {
        if (!result.value) return;

        useService.barcodePoolService
          .deleteBarcodeRange(row.id)
          .then(response => {
            this.showSuccessMessage(response.message || this.$t('Successful'));
            this.loadItems();
          })
          .catch(messages => {
            this.showBackendErrors(messages);
          });
      });
    }
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.barcode-pool-page {
  .bp-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;

    &__subtitle {
      color: $text-muted;
      font-size: 0.95rem;

      strong {
        color: inherit;
        font-weight: 600;
      }

      .bp-dot {
        margin: 0 0.4rem;
        opacity: 0.5;
      }
    }

    .dark-layout &__subtitle {
      color: $theme-dark-body-color;
    }
  }

  .bp-summary {
    margin-bottom: 1.25rem;
  }

  .bp-stat-card {
    background: $white;
    border-radius: 0.75rem;
    padding: 1.5rem;
    box-shadow: 0 4px 20px 0 rgba(34, 41, 47, 0.05);
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.03);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px 0 rgba(34, 41, 47, 0.08);
    }

    &__head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 1.25rem;
    }

    &__icon {
      width: 42px;
      height: 42px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s ease;
    }

    &:hover &__icon {
      transform: scale(1.08);
    }

    &__badge {
      font-weight: 600;
      padding: 0.4rem 0.6rem;
      font-size: 0.75rem;
    }

    &__body {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }

    &__value {
      font-size: 2.1rem;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 0.4rem;
      letter-spacing: -0.02em;
      color: $headings-color;
      display: flex;
      align-items: baseline;
    }

    &__pct {
      font-size: 0.9rem;
      font-weight: 600;
      margin-left: 0.5rem;
    }

    &__label {
      font-size: 0.95rem;
      font-weight: 600;
      color: $text-muted;
      margin-bottom: 0.2rem;
    }

    &__desc {
      font-size: 0.8rem;
      color: $text-muted;
      opacity: 0.8;
    }

    &--success {
      .bp-stat-card__icon {
        background: rgba($success, 0.12);
        color: $success;
      }
      .bp-stat-card__pct {
        color: $success;
      }
    }
    &--warning {
      .bp-stat-card__icon {
        background: rgba($warning, 0.12);
        color: $warning;
      }
    }
    &--primary {
      .bp-stat-card__icon {
        background: rgba($primary, 0.12);
        color: $primary;
      }
    }
    &--dark {
      .bp-stat-card__icon {
        background: rgba($dark, 0.08);
        color: $dark;
      }
    }

    .dark-layout & {
      background: $theme-dark-card-bg;
      border-color: $theme-dark-border-color;
      box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.2);

      &:hover {
        box-shadow: 0 8px 25px 0 rgba(0, 0, 0, 0.3);
      }

      &__value {
        color: $theme-dark-headings-color;
      }

      &__desc,
      &__label,
      &__pct {
        color: $theme-dark-body-color;
      }

      &--success .bp-stat-card__icon {
        background: rgba($success, 0.2);
      }
      &--warning .bp-stat-card__icon {
        background: rgba($warning, 0.2);
      }
      &--primary .bp-stat-card__icon {
        background: rgba($primary, 0.2);
      }
      &--dark .bp-stat-card__icon {
        background: rgba(255, 255, 255, 0.1);
        color: $theme-dark-body-color;
      }
    }
  }

  .bp-ranges-card {
    padding: 0;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 1rem;
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid $border-color;

      h4 {
        font-weight: 600;
        font-size: 1.1rem;
      }

      .dark-layout & {
        border-bottom-color: $theme-dark-border-color;
      }
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__filter {
      padding: 1rem 1.5rem 0;
    }
  }

  .bp-count-pill {
    background: $body-bg !important;
    color: $text-muted !important;
    font-weight: 600;
    font-size: 0.8rem;
    padding: 0.3rem 0.6rem;
    border-radius: 1rem;
    min-width: 50px;
    .dark-layout & {
      background: rgba(255, 255, 255, 0.08) !important;
      color: $theme-dark-body-color !important;
    }
  }

  .bp-search {
    width: 280px;
    max-width: 100%;

    .input-group-text {
      background: transparent;
      border-right: none;
    }

    .form-control {
      border-left: none;
    }
  }

  .bp-empty,
  .bp-error {
    padding: 4rem 2rem;
    text-align: center;

    &__icon {
      opacity: 0.45;
      margin-bottom: 1rem;
    }

    h4,
    h5 {
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
  }

  .bp-empty__icon {
    color: $text-muted;
  }

  .bp-error__icon {
    color: $danger;
  }

  .bp-table {
    th {
      text-transform: uppercase;
      font-size: 0.7rem;
      letter-spacing: 0.05em;
      color: $text-muted;
      font-weight: 600;
    }

    td {
      vertical-align: middle;
      padding: 0.9rem 0.75rem !important;
      transition: background-color 0.15s ease;
    }

    tbody tr:hover td {
      background: rgba($primary, 0.04);
    }

    .dark-layout & tbody tr:hover td {
      background: rgba(255, 255, 255, 0.03);
    }
  }

  .bp-range-cell {
    display: flex;
    flex-direction: column;
    font-family: $font-family-monospace;
    font-size: 0.85rem;

    &__start {
      font-weight: 600;
    }

    &__end {
      color: $text-muted;
      font-size: 0.78rem;
      margin-top: 0.15rem;
    }
  }

  .bp-provider-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;

    &__logo {
      width: 28px;
      height: 28px;
      object-fit: contain;
      flex-shrink: 0;
    }

    &__text {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    &__name {
      font-weight: 600;
    }

    &__type {
      color: $text-muted;
      font-size: 0.78rem;
      margin-top: 0.1rem;
    }
  }

  .bp-ref-link {
    color: $primary;
    font-weight: 500;
    text-decoration: underline;
    text-decoration-color: rgba($primary, 0.4);
    text-underline-offset: 2px;
    transition: color 0.15s ease, text-decoration-color 0.15s ease;

    &:hover {
      color: darken($primary, 10%);
      text-decoration-color: $primary;
    }
  }

  .bp-available-cell {
    display: flex;
    flex-direction: column;
  }

  .bp-consumption-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 180px;

    &__pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.3rem;
      white-space: nowrap;
      min-width: 100px;
    }

    &__dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
      display: inline-block;
    }
  }

  .bp-progress {
    display: flex;
    flex: 1;
    height: 6px;
    background: $body-bg;
    border-radius: 1rem;
    overflow: hidden;
    min-width: 80px;

    &__used {
      background: $primary;
      transition: width 0.3s ease, background-color 0.2s ease;
    }

    &__reserved {
      background: $warning;
      transition: width 0.3s ease, background-color 0.2s ease;
    }

    // Terminal range — desaturate the bar to reinforce the "archived" signal.
    &--completed {
      .bp-progress__used,
      .bp-progress__reserved {
        background: $secondary;
      }
    }

    .dark-layout & {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  .bp-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.7rem;
    font-weight: 500;
    font-size: 0.75rem;

    &__dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }
  }

  .bp-kebab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: $text-muted;

    &:hover {
      background: $body-bg;
      color: $primary;
    }

    .dark-layout &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }
}
</style>
